import { imageQuality } from '@repo/sanity/image'
import { ShopProduct } from '@repo/shop/types'
import { getDiscount, getPriceAmount, getPriceCurrency } from '@repo/shopify/price'
import {
  ProductGridItemCarousel,
  ProductGridItemCarouselItem,
  ProductGridItemInfo,
  ProductGridItemRoot,
  ProductGridItemSwatches,
  ProductGridItemTag,
} from '@repo/ui/components/product-grid-item'
import { cn } from '@repo/ui/lib/utils'
import { createHrefForDocument } from '@repo/url/href'
import { Image } from 'next-sanity/image'
import { LinkProps } from 'next/dist/client/link'
import { AnchorHTMLAttributes, FunctionComponent } from 'react'
import { PageDto } from '../../types/page.types'
import ProductGridAddToWishlistIcon from './ProductGridAddToWishlistIcon'

interface Props {
  pageDto: PageDto
  products: ShopProduct[]
  className?: string
}

const ShopProductGrid: FunctionComponent<Props> = ({ pageDto, products, className }) => {
  return (
    <ul className={cn('grid auto-rows-fr grid-cols-2 items-stretch lg:grid-cols-4', className)}>
      {products.map((product, i) => {
        const urlSlug = String(product.sanityProduct.urlSlug?.current || '')
        const title = String(product.sanityProduct.title || product.shopifyProduct.title || '')
        const amount = getPriceAmount(product.shopifyProduct)
        const currencyCode = getPriceCurrency(product.shopifyProduct)
        const discount = getDiscount(product.shopifyProduct)

        const linkProps: LinkProps & AnchorHTMLAttributes<HTMLAnchorElement> = {
          title,
          href: createHrefForDocument('product', urlSlug, pageDto.params.locale),
        }

        const otherColors = product.sanityProduct.colorProductSet?.flatMap((item) => {
          if (!item.color || item.color === product.sanityProduct.color) {
            return []
          }

          return {
            color: item.color,
            linkProps: {
              title: item.title ? String(item.title) : undefined,
              href: createHrefForDocument('product', item.urlSlug || '', pageDto.params.locale),
            },
          }
        })

        return (
          <ProductGridItemRoot key={i}>
            <ProductGridItemCarousel
              tag={<ProductGridItemTag key={product.sanityProduct._id}>{product.shopifyProduct?.availableForSale && <>{product.sanityProduct.tag}</>}</ProductGridItemTag>}
            >
              {product.sanityProduct.thumbnails?.flatMap((thumbnail) => {
                if (thumbnail?.asset?.url) {
                  return (
                    <ProductGridItemCarouselItem key={thumbnail._key} linkProps={linkProps}>
                      <Image
                        fill={true}
                        src={thumbnail.asset.url}
                        quality={imageQuality}
                        className='object-cover'
                        alt={thumbnail?.alt || title || ''}
                        blurDataURL={thumbnail?.asset?.metadata?.lqip ?? undefined}
                        placeholder={thumbnail?.asset?.metadata?.lqip ? 'blur' : undefined}
                        sizes='(min-width: 1024px) 25vw, 50vw'
                      />
                    </ProductGridItemCarouselItem>
                  )
                }

                return []
              })}
            </ProductGridItemCarousel>
            <ProductGridItemInfo
              linkProps={linkProps}
              price={amount}
              discount={discount}
              title={title}
              currencyCode={currencyCode}
              wishlist={<ProductGridAddToWishlistIcon key={product.sanityProduct._id} pageDto={pageDto} urlSlug={urlSlug} />}
              titleAs='h3'
            >
              {product.sanityProduct.color && (
                <ProductGridItemSwatches
                  others={otherColors}
                  current={{ color: product.sanityProduct.color, linkProps }}
                  label={pageDto.dictionary?.collection?.availableinxcolors || 'Available in %1 color(s).'}
                />
              )}
            </ProductGridItemInfo>
          </ProductGridItemRoot>
        )
      })}
    </ul>
  )
}

export default ShopProductGrid
